<template>
    <div :class="`signature-align-${align} signature`">
        <label for="signature" v-if="!isSpecial" class="field-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        
        <div :class="['signature-container', { 'error': shouldShowError }]" v-if="!isSpecial">
            <div class="signature-canvas-wrapper" :style="canvasWrapperStyle">
                <canvas
                    ref="signatureCanvas"
                    :width="canvasWidth"
                    :height="canvasHeight"
                    :style="canvasStyle"
                    @mousedown="startDrawing"
                    @mousemove="draw"
                    @mouseup="stopDrawing"
                    @mouseleave="stopDrawing"
                    @touchstart="startDrawing"
                    @touchmove="draw"
                    @touchend="stopDrawing"
                ></canvas>
                
                <div v-if="isEmpty && !isDrawing" class="signature-placeholder">
                    {{ placeholder }}
                </div>
                
                <button
                    v-if="showClearButton && !isEmpty" 
                    @click="clearSignature"
                    class="clear-button"
                    type="button"
                >
                    {{ clearButtonText }}
                </button>
            </div>
            
            <div v-if="altLabel" class="alt-label">{{ altLabel }}</div>
        </div>
        
        <div v-if="shouldShowError && !isSpecial" class="error-message">
            {{ fieldLabel }} alanı zorunludur
        </div>
        
        <div v-if="isSpecial" class="special-signature-wrapper">
            <div class="special-signature-container" :style="canvasWrapperStyle">
                <canvas
                    ref="specialSignatureCanvas"
                    :width="canvasWidth"
                    :height="canvasHeight"
                    :style="canvasStyle"
                    @mousedown="startDrawing"
                    @mousemove="draw"
                    @mouseup="stopDrawing"
                    @mouseleave="stopDrawing"
                    @touchstart="startDrawing"
                    @touchmove="draw"
                    @touchend="stopDrawing"
                ></canvas>
                
                <div v-if="isEmpty && !isDrawing" class="special-signature-placeholder">
                    {{ fieldLabel }}
                </div>
                
                <button 
                    v-if="showClearButton && !isEmpty" 
                    @click="clearSignature"
                    class="special-clear-button"
                    type="button"
                >
                    {{ clearButtonText }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormSignature",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            default: "top",
        },
        altLabel: {
            type: String,
            default: "",
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        canvasWidth: {
            type: Number,
            default: 400
        },
        canvasHeight: {
            type: Number,
            default: 200
        },
        strokeColor: {
            type: String,
            default: '#000000'
        },
        strokeWidth: {
            type: Number,
            default: 2
        },
        backgroundColor: {
            type: String,
            default: '#ffffff'
        },
        borderColor: {
            type: String,
            default: '#e5e7eb'
        },
        borderWidth: {
            type: Number,
            default: 1
        },
        borderRadius: {
            type: Number,
            default: 8
        },
        showClearButton: {
            type: Boolean,
            default: true
        },
        clearButtonText: {
            type: String,
            default: 'Temizle'
        },
        placeholder: {
            type: String,
            default: 'Burada imza çizin'
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        shouldShowError: {
            type: Boolean,
            default: false,
        },
        isSpecial: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isDrawing: false,
            isEmpty: true,
            lastX: 0,
            lastY: 0,
            ctx: null,
            signatureData: '',
            saveTimer: null,
        };
    },
    computed: {
        canvasWrapperStyle() {
            return {
                width: `${this.canvasWidth}px`,
                height: `${this.canvasHeight}px`,
                backgroundColor: this.backgroundColor,
                borderColor: this.borderColor,
                borderWidth: `${this.borderWidth}px`,
                borderRadius: `${this.borderRadius}px`,
            };
        },
        canvasStyle() {
            return {
                cursor: 'crosshair',
                borderRadius: `${this.borderRadius}px`,
            };
        }
    },
    mounted() {
        this.initCanvas();
        
        // Kısa bir delay sonra restore et - DOM'un hazır olması için
        this.$nextTick(() => {
            setTimeout(() => {
                this.restoreSignatureFromFormValue();
            }, 100);
        });
        
        console.log('🔴 Visitor Signature mounted:', {
            fieldLabel: this.fieldLabel,
            formId: this.formId,
            order: this.order,
            canvasWidth: this.canvasWidth,
            canvasHeight: this.canvasHeight,
            hasExistingSignature: !!this.signatureData
        });
    },
    methods: {
        initCanvas() {
            const canvas = this.isSpecial ? this.$refs.specialSignatureCanvas : this.$refs.signatureCanvas;
            if (!canvas) return;
            
            this.ctx = canvas.getContext('2d');
            this.ctx.lineCap = 'round';
            this.ctx.lineJoin = 'round';
            this.ctx.strokeStyle = this.strokeColor;
            this.ctx.lineWidth = this.strokeWidth;
            
            this.ctx.fillStyle = this.backgroundColor;
            this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
        },
        
        getEventPos(e) {
            const canvas = this.isSpecial ? this.$refs.specialSignatureCanvas : this.$refs.signatureCanvas;
            const rect = canvas.getBoundingClientRect();
            const clientX = e.clientX || (e.touches && e.touches[0].clientX);
            const clientY = e.clientY || (e.touches && e.touches[0].clientY);
            
            return {
                x: clientX - rect.left,
                y: clientY - rect.top
            };
        },
        
        startDrawing(e) {
            e.preventDefault();
            this.isDrawing = true;
            this.isEmpty = false;
            
            const pos = this.getEventPos(e);
            this.lastX = pos.x;
            this.lastY = pos.y;
        },
        
        draw(e) {
            if (!this.isDrawing) return;
            e.preventDefault();
            
            const pos = this.getEventPos(e);
            
            this.ctx.beginPath();
            this.ctx.moveTo(this.lastX, this.lastY);
            this.ctx.lineTo(pos.x, pos.y);
            this.ctx.stroke();
            
            this.lastX = pos.x;
            this.lastY = pos.y;
            
            // Her çizim sonrası signature'ı kaydet (throttled)
            this.throttledSave();
        },
        
        stopDrawing(e) {
            if (!this.isDrawing) return;
            e.preventDefault();
            
            this.isDrawing = false;
            this.saveSignature();
        },
        
        /**
         * Throttled save - çok sık kayıt yapmamak için
         */
        throttledSave() {
            if (this.saveTimer) {
                clearTimeout(this.saveTimer);
            }
            this.saveTimer = setTimeout(() => {
                this.saveSignature();
            }, 100); // 100ms throttle
        },
        
        /**
         * LocalStorage'a imza verisini kaydet
         */
        saveToLocalStorage() {
            try {
                const storageKey = `signature_${this.formId}_${this.order}`;
                localStorage.setItem(storageKey, this.signatureData);
                console.log('💾 Signature saved to localStorage:', storageKey);
            } catch (error) {
                console.error('❌ Error saving signature to localStorage:', error);
            }
        },
        
        /**
         * LocalStorage'dan imza verisini al
         */
        loadFromLocalStorage() {
            try {
                const storageKey = `signature_${this.formId}_${this.order}`;
                const savedData = localStorage.getItem(storageKey);
                
                if (savedData && savedData.startsWith('data:image')) {
                    this.signatureData = savedData;
                    this.isEmpty = false;
                    
                    console.log('💾 Signature loaded from localStorage:', {
                        storageKey: storageKey,
                        dataLength: savedData.length
                    });
                    
                    return savedData;
                }
            } catch (error) {
                console.error('❌ Error loading signature from localStorage:', error);
            }
            
            return null;
        },
        
        /**
         * LocalStorage'dan imza verisini temizle
         */
        clearFromLocalStorage() {
            try {
                const storageKey = `signature_${this.formId}_${this.order}`;
                localStorage.removeItem(storageKey);
                console.log('🗑️ Signature cleared from localStorage:', storageKey);
            } catch (error) {
                console.error('❌ Error clearing signature from localStorage:', error);
            }
        },
        
        clearSignature() {
            if (!this.ctx) return;
            
            this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
            this.ctx.fillStyle = this.backgroundColor;
            this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
            
            this.isEmpty = true;
            this.signatureData = '';
            
            // LocalStorage'dan da temizle
            this.clearFromLocalStorage();
            
            this.emitValues();
        },
        
        saveSignature() {
            const canvas = this.isSpecial ? this.$refs.specialSignatureCanvas : this.$refs.signatureCanvas;
            if (canvas) {
                const newSignatureData = canvas.toDataURL('image/png');
                
                // Sadece değişiklik varsa emit et
                if (newSignatureData !== this.signatureData) {
                    this.signatureData = newSignatureData;
                    
                    // LocalStorage'a da kaydet (sayfalama için)
                    this.saveToLocalStorage();
                    
                    this.emitValues();
                    
                    console.log('🔴 Visitor signature saved and emitted:', {
                        fieldId: this.formId,
                        dataLength: this.signatureData.length,
                        preview: this.signatureData.substring(0, 50) + '...',
                        savedToLocalStorage: true
                    });
                }
            }
        },
        
        emitValues() {
            this.$emit("input", {
                order: this.order,
                value: this.signatureData,
                isSignature: true,
            });
        },
        
        /**
         * Mevcut imza verisini restore et (önce localStorage, sonra FormContainer)
         */
        restoreSignatureFromFormValue() {
            // 1. Önce localStorage'dan bak
            const localStorageData = this.loadFromLocalStorage();
            if (localStorageData) {
                // Canvas'a imzayı çiz
                this.$nextTick(() => {
                    this.loadSignatureToCanvas(localStorageData);
                });
                
                // FormContainer'a da emit et
                this.emitValues();
                
                console.log('✅ Signature restored from localStorage');
                return;
            }
            
            // 2. LocalStorage'da yoksa FormContainer'dan bak
            let formValue = null;
            
            // Birkaç farklı yöntemle parent'a erişmeyi dene
            if (this.$parent && this.$parent.formValues) {
                formValue = this.$parent.formValues[this.formId];
            } else if (this.$root && this.$root.$children) {
                // FormContainer'ı bul
                const formContainer = this.$root.$children.find(child => 
                    child.$options.name === 'FormContainer' || 
                    (child.formValues && typeof child.formValues === 'object')
                );
                if (formContainer && formContainer.formValues) {
                    formValue = formContainer.formValues[this.formId];
                }
            }
            
            console.log('🔍 Signature restore attempt from FormContainer:', {
                fieldId: this.formId,
                formValue: formValue,
                parentExists: !!this.$parent,
                parentHasFormValues: !!(this.$parent && this.$parent.formValues)
            });
            
            if (formValue && formValue.value && typeof formValue.value === 'string' && formValue.value.startsWith('data:image')) {
                this.signatureData = formValue.value;
                this.isEmpty = false;
                
                // LocalStorage'a da kaydet
                this.saveToLocalStorage();
                
                // Canvas'a imzayı çiz
                this.$nextTick(() => {
                    this.loadSignatureToCanvas(formValue.value);
                });
                
                console.log('✅ Signature restored from FormContainer and saved to localStorage');
            } else {
                console.log('❌ No valid signature found anywhere for field:', this.formId);
            }
        },
        
        /**
         * Base64 imza verisini canvas'a yükle
         */
        loadSignatureToCanvas(base64Data) {
            if (!base64Data || !base64Data.startsWith('data:image')) {
                console.log('❌ Invalid signature data for canvas load');
                return;
            }
            
            const canvas = this.isSpecial ? this.$refs.specialSignatureCanvas : this.$refs.signatureCanvas;
            if (!canvas) {
                console.log('❌ Canvas ref not found for signature load');
                return;
            }
            
            if (!this.ctx) {
                console.log('❌ Canvas context not available, reinitializing...');
                this.initCanvas();
                if (!this.ctx) return;
            }
            
            const img = new Image();
            img.onload = () => {
                try {
                    // Canvas'ı temizle
                    this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                    this.ctx.fillStyle = this.backgroundColor;
                    this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
                    
                    // İmzayı çiz
                    this.ctx.drawImage(img, 0, 0, this.canvasWidth, this.canvasHeight);
                    
                    console.log('✅ Signature image loaded to canvas successfully');
                } catch (error) {
                    console.error('❌ Error loading signature to canvas:', error);
                }
            };
            img.onerror = () => {
                console.error('❌ Error loading signature image');
            };
            img.src = base64Data;
        },
    },
    watch: {
        strokeColor(newVal) {
            if (this.ctx) {
                this.ctx.strokeStyle = newVal;
            }
        },
        strokeWidth(newVal) {
            if (this.ctx) {
                this.ctx.lineWidth = newVal;
            }
        },
        backgroundColor(newVal) {
            if (this.ctx) {
                this.ctx.fillStyle = newVal;
                this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
            }
        },
        canvasWidth() {
            this.$nextTick(() => {
                this.initCanvas();
            });
        },
        canvasHeight() {
            this.$nextTick(() => {
                this.initCanvas();
            });
        }
    },
    beforeDestroy() {
        // Timer'ı temizle
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
        
        // Son kez kaydet
        if (this.signatureData) {
            this.emitValues();
            console.log('🔴 Final signature emit before destroy');
        }
    }
};
</script>

<style lang="scss" scoped>
.signature {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.75rem, 2vw, 1.25rem) auto;
    padding: clamp(0.75rem, 2vw, 1rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    backdrop-filter: blur(20px);
    border-radius: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    .field-label {
        display: block;
        font-size: clamp(1rem, 2.2vw, 1.125rem);
        font-weight: 600;
        color: rgba(55, 65, 81, 0.9);
        margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.25rem;
            font-weight: 700;
        }
    }

    .signature-container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: clamp(0.5rem, 1.2vw, 0.75rem);
        
        &.error {
            .signature-canvas-wrapper {
                border-color: #ef4444 !important;
                box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
            }
        }
    }

    .signature-canvas-wrapper {
        position: relative;
        border: solid;
        border-style: solid;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        transition: all 0.3s ease;
        
        &:hover {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        
        canvas {
            display: block;
            touch-action: none;
            user-select: none;
        }
    }

    .signature-placeholder {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: rgba(107, 114, 128, 0.6);
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 500;
        pointer-events: none;
        text-align: center;
        z-index: 1;
    }

    .clear-button {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        padding: clamp(0.375rem, 1vw, 0.5rem) clamp(0.75rem, 1.5vw, 1rem);
        font-size: clamp(0.75rem, 1.5vw, 0.875rem);
        font-weight: 600;
        color: #ffffff;
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        border: none;
        border-radius: 0.375rem;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 2;
        
        &:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }
        
        &:active {
            transform: translateY(0);
        }
    }

    .alt-label {
        font-size: clamp(0.75rem, 1.5vw, 0.875rem);
        color: rgba(107, 114, 128, 0.7);
        text-align: center;
        margin-top: clamp(0.25rem, 0.8vw, 0.5rem);
    }

    .error-message {
        font-size: clamp(0.75rem, 1.5vw, 0.875rem);
        color: #ef4444;
        margin-top: clamp(0.375rem, 1vw, 0.5rem);
        text-align: center;
        font-weight: 500;
    }
}

.special-signature-wrapper {
    width: 100%;
    
    .special-signature-container {
        position: relative;
        border: solid;
        border-style: solid;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        margin: 0 auto;
        
        canvas {
            display: block;
            touch-action: none;
            user-select: none;
        }
    }
    
    .special-signature-placeholder {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: rgba(107, 114, 128, 0.6);
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 500;
        pointer-events: none;
        text-align: center;
        z-index: 1;
    }
    
    .special-clear-button {
        position: absolute;
        top: 0.25rem;
        right: 0.25rem;
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        font-weight: 600;
        color: #ffffff;
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        border: none;
        border-radius: 0.25rem;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 2;
        
        &:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }
    }
}

.signature-align-top {
    .field-label {
        text-align: left;
    }
}

.signature-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    
    .field-label {
        flex-shrink: 0;
        margin-bottom: 0;
        margin-top: clamp(0.5rem, 1.2vw, 0.75rem);
    }
    
    .signature-container {
        flex: 1;
    }
}

.signature-align-center {
    .field-label {
        text-align: center;
    }
}

.signature-align-right {
    .field-label {
        text-align: right;
    }
}

@media (max-width: 768px) {
    .signature {
        margin: 0.5rem auto;
        padding: 0.75rem;
        
        .signature-canvas-wrapper,
        .special-signature-container {
            max-width: 100%;
            
            canvas {
                max-width: 100%;
                height: auto;
            }
        }
        
        .clear-button {
            padding: 0.375rem 0.75rem;
            font-size: 0.75rem;
        }
    }
    
    .signature-align-left {
        flex-direction: column;
        
        .field-label {
            margin-bottom: 0.5rem;
            margin-top: 0;
        }
    }
}

@media (max-width: 480px) {
    .signature {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .signature-canvas-wrapper,
        .special-signature-container {
            width: 100% !important;
            min-height: 150px;
            
            canvas {
                width: 100% !important;
                max-width: 100% !important;
            }
        }
    }
}

.signature {
    will-change: transform;
    
    .signature-canvas-wrapper,
    .special-signature-container {
        will-change: box-shadow;
    }
    
    canvas {
        will-change: auto;
    }
}

@media (prefers-reduced-motion: reduce) {
    .signature {
        transition: none;
        
        &:hover {
            transform: none;
        }
    }
    
    .clear-button,
    .special-clear-button {
        transition: none;
        
        &:hover {
            transform: none;
        }
    }
}
</style>
